import { Badge, Body1, Caption1, Divider, makeStyles, tokens } from "@fluentui/react-components";
import { CopyIconButton } from "@/components/copy-icon-button";
import { SectionTitle } from "@/components/ui-typography";
import { Briefcase16Regular, Mail16Regular, Warning12Regular } from "@fluentui/react-icons";
import { type ReactNode } from "react";
import { formatDateRange } from "@/lib/date-format";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { fmtCurrency, fmtMM } from "@/lib/format";
import type { ResourceWorkloadSummary } from "@/store/catalog-store";
import type { OrganizationTeam, UserResource } from "@/engine/types";
import { EMPLOYMENT_LABEL, GRADE_LABEL } from "./resource-meta";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  head: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalM,
  },
  headText: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
    flex: 1,
  },
  badges: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
    alignItems: "center",
  },
  kv: {
    display: "grid",
    gridTemplateColumns: "88px 1fr",
    rowGap: tokens.spacingVerticalXS,
    columnGap: tokens.spacingHorizontalS,
    alignItems: "center",
  },
  k: { color: tokens.colorNeutralForeground3 },
  v: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  emailValue: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    minWidth: 0,
  },
  mailIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  skills: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
  skill: {
    fontSize: "11px",
    backgroundColor: tokens.colorNeutralBackground3,
    padding: "1px 6px",
    borderRadius: "10px",
    color: tokens.colorNeutralForeground2,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    lineHeight: 1,
  },
  sectionTitleSide: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    lineHeight: 1,
    "& svg": { display: "block" },
  },
  asgList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  asgRow: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground2Hover },
  },
  asgRowStatic: {
    cursor: "default",
    ":hover": { backgroundColor: tokens.colorNeutralBackground2 },
  },
  asgTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
  },
  asgName: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  over: {
    color: tokens.colorPaletteRedForeground1,
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    lineHeight: 1,
    "& svg": { display: "block" },
  },
  actions: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
});

/**
 * Read-only profile with cross-project workload. Shared by the resource pool drawer, the
 * 인력 & 팀 page and the assignment dialog so a person always looks the same everywhere.
 */
export function ResourceProfilePanel({
  resource,
  team,
  workload,
  masked,
  onOpenProject,
  actions,
  compact,
}: {
  resource: UserResource;
  team?: OrganizationTeam;
  workload: ResourceWorkloadSummary;
  masked?: boolean;
  onOpenProject?: (projectId: string) => void;
  actions?: ReactNode;
  compact?: boolean;
}) {
  const styles = useStyles();
  const pct = Math.round(workload.totalAllocationRatio * 100);
  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <PersonAvatar name={resource.displayName} size={compact ? 40 : 56} />
        <div className={styles.headText}>
          <SectionTitle>{resource.displayName}</SectionTitle>
          <Body1 style={{ color: tokens.colorNeutralForeground2 }}>{resource.roleTitle}</Body1>
          <div className={styles.badges}>
            {team && (
              <Badge appearance="tint" color="subtle">
                {team.name}
              </Badge>
            )}
            <Badge appearance="tint" color="subtle">
              {EMPLOYMENT_LABEL[resource.employmentType]}
            </Badge>
            <Badge appearance="tint" color="subtle">
              {GRADE_LABEL[resource.grade]}
            </Badge>
            {!resource.isActive && <Badge appearance="tint" color="warning">비활성</Badge>}
          </div>
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>

      <div className={styles.kv}>
        <Caption1 className={styles.k}>이메일</Caption1>
        <div className={styles.emailValue}>
          <Mail16Regular className={styles.mailIcon} />
          <Caption1 className={styles.v} title={resource.email || undefined}>
            {resource.email || "—"}
          </Caption1>
          {resource.email ? <CopyIconButton value={resource.email} label="이메일 복사" /> : null}
        </div>
        <Caption1 className={styles.k}>기준 월 단가</Caption1>
        <Caption1 className={styles.v}>{fmtCurrency(resource.defaultMonthlyRate, masked)}</Caption1>
        {resource.notes && (
          <>
            <Caption1 className={styles.k}>메모</Caption1>
            <Caption1 className={styles.v} title={resource.notes}>
              {resource.notes}
            </Caption1>
          </>
        )}
      </div>

      {resource.skills.length > 0 && (
        <div className={styles.skills}>
          {resource.skills.map((s) => (
            <span key={s} className={styles.skill}>
              {s}
            </span>
          ))}
        </div>
      )}

      <Divider />

      <div className={styles.sectionTitle}>
        <Caption1 className={styles.sectionTitleSide}>
          총 가동률 {pct}%
          {workload.isOverAllocated && (
            <span className={styles.over}>
              <Warning12Regular /> 과부하
            </span>
          )}
        </Caption1>
        <Caption1 className={styles.sectionTitleSide} style={{ color: tokens.colorNeutralForeground3 }}>
          {workload.projectCount > 0 ? fmtMM(workload.totalPlannedMM) : "투입 대기 중"}
        </Caption1>
      </div>

      {workload.assignments.length > 0 && (
        <div className={styles.asgList}>
          {workload.assignments.map((a) => (
              <div
                key={`${a.projectId}-${a.projectRole}`}
                className={`${styles.asgRow} ${onOpenProject ? "" : styles.asgRowStatic}`}
                onClick={() => onOpenProject?.(a.projectId)}
                role={onOpenProject ? "button" : undefined}
              >
                <div className={styles.asgTop}>
                  <span className={styles.asgName}>
                    <Briefcase16Regular style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
                    <span>{a.projectName}</span>
                  </span>
                  <Caption1 style={{ flexShrink: 0 }}>{Math.round(a.allocationRatio * 100)}%</Caption1>
                </div>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  {a.projectRole} · {formatDateRange(a.startDate, a.endDate, "numeric")} · {fmtMM(a.plannedMM)}
                </Caption1>
              </div>
          ))}
        </div>
      )}
    </div>
  );
}
